type CsvRow<Header extends string> = Record<Header, string>;

const splitCsvLine = (line: string): string[] => {
  const cells: string[] = [];
  let currentCell = '';
  let isQuoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    const nextCharacter = line[index + 1];

    if (character === '"') {
      if (isQuoted && nextCharacter === '"') {
        currentCell += '"';
        index += 1;
      } else {
        isQuoted = !isQuoted;
      }
      continue;
    }

    if (character === ',' && !isQuoted) {
      cells.push(currentCell.trim());
      currentCell = '';
      continue;
    }

    currentCell += character;
  }

  cells.push(currentCell.trim());
  return cells;
};

/** Parses a CSV whose first row contains column names into typed records. */
export const parseCsv = <Header extends string>(
  csvContent: string,
  requiredHeaders: readonly Header[]
): CsvRow<Header>[] => {
  const lines = csvContent.replace(/^\uFEFF/, '').split(/\r?\n/);
  const headers = splitCsvLine(lines[0] ?? '');
  const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));

  if (missingHeaders.length > 0) {
    throw new Error(`Missing CSV headers: ${missingHeaders.join(', ')}`);
  }

  return lines.slice(1).flatMap(line => {
    if (!line.trim()) {
      return [];
    }

    const cells = splitCsvLine(line);
    if (cells.every(cell => cell === '')) {
      return [];
    }

    return [
      Object.fromEntries(
        requiredHeaders.map(header => [header, cells[headers.indexOf(header)] ?? ''])
      ) as CsvRow<Header>,
    ];
  });
};
